import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../../GlobalManagement/Pages/Sidebar";

import toast from "react-hot-toast";
import Pagination from "../../GlobalManagement/Pages/Pagination";
import { Bell, ArrowLeft,Trash2 } from "lucide-react";
import { 
  masterBodyArea, 
  masterContentArea, 
  masterPageShell, 
  masterHeader, 
  masterHeaderCard, 
  masterHeaderInfo, 
  masterHeaderIcon, 
  masterTitle, 
  masterPrimaryButton, 
  masterTableBody, 
  masterPanel, 
  masterTable, 
  masterStickyHeadRow, 
  masterTableData, 
  masterTbody, 
  masterTableRow, 
  masterTableCell, 
  masterDeleteButton, 
  masterActionButtonsContainer 
} from '../../GlobalManagement/Pages/masterPageStyles';

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  
  // If it's already in DD-MM-YYYY format, return it as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    return dateStr;
  }

  // If it's a standard YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  }

  // If it's a full JS Date string (e.g., "Mon Feb 16 2026..."), parse it safely
  const parsedDate = new Date(dateStr);
  if (!Number.isNaN(parsedDate.getTime())) {
    const day = String(parsedDate.getDate()).padStart(2, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const year = parsedDate.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return dateStr;
};
const parseStatusDate = (value) => {
  if (!value || typeof value !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(value);
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [dd, mm, yyyy] = value.split("-");
    return new Date(`${yyyy}-${mm}-${dd}`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Updated status flow as per your 2 phases
const STATUS_RULES = {
  // Phase 1: Fresher Flow
  TP: { months: 6, next: "TR" },
  TR: { months: 6, next: "TEP" },
  TEP: { months: 6, next: "P" },
  
  // Phase 2: Experienced Flow & Doctor Flow
  PB: { months: 6, next: "P" },
  PDP: { months: 6, next: "PD" },
  
  // Permanent / Ex-Employee (No next auto-status)
  P: null,
  PD: null,
  EX: null
};

const STATUS_LABELS = {
  TP: "Trainee Probation",
  TR: "Trainee",
  TEP: "Trainee Employee Probation",
  PB: "Probation",
  P: "Permanent",
  PDP: "Permanent Doctor Probation",
  PD: "Permanent Doctor",
  EX: "Ex-Employee",
};

const ReminderPage = () => {

  const [reminderList, setReminderList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [activeTab, setActiveTab] = useState("reminder");
  
  const [historyFilter, setHistoryFilter] = useState("");
const goBack = () => navigate(-1);
  const navigate = useNavigate();
  const location = useLocation();

  // Pagination states
  const [currentPageReminder, setCurrentPageReminder] = useState(1);
  const [currentPageHistory, setCurrentPageHistory] = useState(1);

  const perPage = 20;

  useEffect(() => {
    fetchReminderData();
  }, []);

  const fetchReminderData = async () => {
    try {
      const token = localStorage.getItem("token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const res = await axios.get("/api/employees", config);
      const today = new Date();

      // Process Reminders
      const reminders = res.data
        .filter(emp => STATUS_RULES[emp.employmentStatus] && STATUS_RULES[emp.employmentStatus].next !== null)
        .map(emp => {
          const rule = STATUS_RULES[emp.employmentStatus];
          const statusChangeDate = parseStatusDate(emp.statusChangeDate);
          const isValidStatusDate = statusChangeDate instanceof Date && !Number.isNaN(statusChangeDate.getTime());
          let remainingDays = null;

          if (isValidStatusDate) {
            const endDate = new Date(statusChangeDate);
            endDate.setMonth(endDate.getMonth() + rule.months);
            remainingDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
          }

          return {
            rawEmployee: emp,
            employeeID: emp.employeeID,
            name: `${emp.firstName} ${emp.middleName || ""} ${emp.lastName}`,
            department: emp.departmentName,
            designation: emp.designationName,
            statusChangeDate: emp.statusChangeDate,
            currentStatus: STATUS_LABELS[emp.employmentStatus] || emp.employmentStatus,
            nextStatus: STATUS_LABELS[rule.next] || rule.next,
            remainingDays,
            changeAvailable: remainingDays !== null && remainingDays <= 0,
          };
          
        })
        .sort((a, b) => {
          if (a.changeAvailable !== b.changeAvailable) {
            return a.changeAvailable ? -1 : 1;
          }
          return (a.remainingDays || 0) - (b.remainingDays || 0);
        });
        
      const history = [];

   res.data.forEach(emp => {
        if (!emp.statusHistory || !Array.isArray(emp.statusHistory) || emp.statusHistory.length === 0) return;

        emp.statusHistory.forEach(h => {
          const oldStatus = h.beforeStatus || h.oldStatus || "";
          const newStatus = h.currentStatus || h.newStatus || "";
          const oldDate = h.beforeDate || h.date || "";
          const newDate = h.currentDate || h.date || "";

          history.push({
            _id: h._id || Math.random().toString(),
            employeeID: emp.employeeID || "",
            name: `${emp.firstName || ""} ${emp.middleName || ""} ${emp.lastName || ""}`.trim(),
            beforeStatus: STATUS_LABELS[oldStatus] || oldStatus,
            beforeDate: formatDate(oldDate),
            currentStatus: STATUS_LABELS[newStatus] || newStatus,
            currentDate: formatDate(newDate),
          });
        });
      });

      setReminderList(reminders);
      setHistoryList(history);
    } catch (err) {
      toast.error("Failed to load data");
    }
  };

  const handleDeleteHistory = async (employeeID, historyID) => {
    if (!window.confirm("Are you sure you want to delete this status history entry?")) return;

    try {
      const token = localStorage.getItem("token");
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      await axios.delete(`/api/employees/${employeeID}/history/${historyID}`, config);
      toast.success("Status history entry deleted successfully");

      setHistoryList(prev =>
        prev.filter(h => h._id !== historyID)
      );
    } catch (err) {
      toast.error("Failed to delete history");
    }
  };

const handleGoChange = (employee) => {
    navigate("/EmployeeMaster", {
      state: { employee: employee, fromReminder: true },
    });
  };

  // For Reminder tab
  const startIndexReminder = (currentPageReminder - 1) * perPage;
  const paginatedReminders = reminderList.slice(
    startIndexReminder,
    startIndexReminder + perPage
  );

  // Filter history if input is present
  const filteredHistory = historyList.filter((h) =>
    h.employeeID.toUpperCase().includes(historyFilter)
  );

  // If filter is applied, show all matching entries, otherwise paginate
  const displayedHistory = historyFilter
    ? filteredHistory
    : historyList.slice(
        (currentPageHistory - 1) * perPage,
        currentPageHistory * perPage
      );

  return (
    <div className={masterBodyArea}>
      <Sidebar />
      <div className={`${masterPageShell} ${masterContentArea} p-1 sm:p-2 md:p-3 pt-12 md:pt-0`}>
        
        {/* Header Section */}
        <header className={masterHeader}>
          <div className={masterHeaderCard}>
            <div className={masterHeaderInfo}>
              <div className={masterHeaderIcon}><Bell size={14} /></div>
              <div><h1 className={masterTitle}>Employee Status Reminder</h1></div>
            </div>
            <button 
              type="button"
              onClick={goBack} 
              className={`${masterPrimaryButton} shrink-0 inline-flex items-center gap-1`}
            >
              <ArrowLeft size={10} /> BACK
            </button>
          </div>
        </header>

        <main className={`${masterTableBody} mt-1 w-full pb-6`}>
          <div className={`${masterPanel} flex flex-col w-full`}>
            
            {/* Tab Navigation & Toolbar */}
            <div className="border-b border-slate-200 bg-white p-2 flex flex-col sm:flex-row gap-2 justify-between items-center">
              <div className="flex gap-2 w-full sm:w-auto">
                <button 
                  onClick={() => setActiveTab("reminder")} 
                  className={`px-4 py-1.5 rounded text-xs font-bold transition-all uppercase ${activeTab === "reminder" ? "bg-[#0a2540] text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                >
                  Reminder ({reminderList.length})
                </button>
                {/* <button 
                  onClick={() => setActiveTab("history")} 
                  className={`px-4 py-1.5 rounded text-xs font-bold transition-all uppercase ${activeTab === "history" ? "bg-[#0a2540] text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                >
                  History ({historyList.length})
                </button> */}
              </div>

              {activeTab === "history" && (
                <div className="w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search by Employee ID..."
                    value={historyFilter}
                    onChange={(e) => setHistoryFilter(e.target.value.toUpperCase())}
                    className="w-full px-3 py-1 border border-slate-300 rounded text-xs font-semibold uppercase outline-none focus:border-[#00b4d8] bg-white text-slate-800"
                  />
                </div>
              )}
            </div>

            {/* Table Container */}
            <div className="overflow-x-auto w-full">
              {activeTab === "reminder" && (
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      <th className={masterTableData}>Employee ID</th>
                      <th className={masterTableData} style={{ textAlign: 'left' }}>Name</th>
                      <th className={masterTableData} style={{ textAlign: 'left' }}>Designation</th>
                      <th className={masterTableData}>Current Status</th>
                      <th className={masterTableData}>Days Left</th>
                      <th className={masterTableData}>Next Status</th>
                      <th className={masterTableData} style={{ width: '110px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {paginatedReminders.length > 0 ? (
                      paginatedReminders.map((emp, index) => (
                        <tr key={index} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {startIndexReminder + index + 1}
                          </td>
                          <td className={masterTableCell} style={{ fontFamily: 'monospace', color: '#00b4d8' }}>
                            {emp.employeeID}
                          </td>
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {emp.name}
                          </td>
                          <td className={masterTableCell} style={{ textAlign: 'left' }}>
                            {emp.designation}
                          </td>
                          <td className={masterTableCell}>{emp.currentStatus}</td>
                          <td className={`masterTableCell font-bold ${emp.changeAvailable ? "text-orange-600" : "text-emerald-600"}`}>
                            {emp.changeAvailable ? "Eligible for Change" : (emp.remainingDays === null ? "-" : emp.remainingDays)}
                          </td>
                          <td className={masterTableCell}>{emp.nextStatus}</td>
                          <td className={masterTableCell}>
                            {emp.changeAvailable && (
                              <button 
                                onClick={() => handleGoChange(emp.rawEmployee)} 
                                className="bg-orange-500 hover:bg-orange-600 text-white px-2.5 py-1 rounded text-[10px] font-bold uppercase shadow-sm transition-all"
                              >
                                GO TO CHANGE
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No eligibility for status change found.
                        </td>
                      </tr>
                    )} 
                  </tbody>
                </table>
              )}

              {activeTab === "history" && (
                <table className={masterTable}>
                  <thead>
                    <tr className={masterStickyHeadRow}>
                      <th className={masterTableData} style={{ width: '60px' }}>S.No</th>
                      <th className={masterTableData}>Employee ID</th>
                      <th className={masterTableData} style={{ textAlign: 'left' }}>Name</th>
                      <th className={masterTableData}>Before Status</th>
                      <th className={masterTableData}>Before Date</th>
                      <th className={masterTableData}>Current Status</th>
                      <th className={masterTableData}>Current Date</th>
                      <th className={masterTableData} style={{ width: '90px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody className={masterTbody}>
                    {displayedHistory.length > 0 ? (
                      displayedHistory.map((h, i) => (
                        <tr key={i} className={masterTableRow}>
                          <td className={masterTableCell}>
                            {historyFilter ? i + 1 : (currentPageHistory - 1) * perPage + i + 1}
                          </td>
                          <td className={masterTableCell} style={{ fontFamily: 'monospace', color: '#00b4d8' }}>
                            {h.employeeID}
                          </td>
                          <td className={masterTableCell} style={{ textAlign: 'left', fontWeight: '600' }}>
                            {h.name}
                          </td>
                          <td className={masterTableCell} style={{ fontWeight: '600', color: '#ff6b6b' }}>
                            {h.beforeStatus}
                          </td>
                          <td className={masterTableCell} style={{ fontWeight: '600', color: '#ff6b6b' }}>
                            {h.beforeDate}
                          </td>
                          <td className={masterTableCell} style={{ fontWeight: '600', color: '#00b4d8' }}>
                            {h.currentStatus}
                          </td>
                          <td className={masterTableCell} style={{ fontWeight: '600', color: '#00b4d8' }}>
                            {h.currentDate}
                          </td>
                          <td className={masterTableCell}>
                            <div className={masterActionButtonsContainer}>
                              <button
                                onClick={() => handleDeleteHistory(h.employeeID, h._id)}
                                className={masterDeleteButton}
                                title="Delete History Entry"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="p-12 text-center text-slate-300 font-bold uppercase text-xs">
                          No status history entries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination Controls Footer */}
            <div className="p-2 border-t border-slate-200 bg-white">
              {activeTab === "reminder" && (
                <Pagination
                  total={reminderList.length}
                  perPage={perPage}
                  currentPage={currentPageReminder}
                  onPageChange={(page) => setCurrentPageReminder(page)}
                />
              )}

              {activeTab === "history" && !historyFilter && (
                <Pagination
                  total={historyList.length}
                  perPage={perPage}
                  currentPage={currentPageHistory}
                  onPageChange={(page) => setCurrentPageHistory(page)}
                />
              )}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default ReminderPage;